import Box from '@mui/material/Box';
import { ThemedIcon } from 'components/icons';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import { useThemeApp } from 'components/Theme/theme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useState, forwardRef, useEffect } from "react";
import { masks, MasksType } from "common/src/other/masks";
import InputAdornment from '@mui/material/InputAdornment';
import type { TextFieldProps } from '@mui/material/TextField';
import { ThemedInputContainer, ThemedInputContainerProps } from "../ThemedInputContainer";

export type InputProps =
    Omit<TextFieldProps, "label" | "placeholder" | "onChange" | "error"> & Omit<ThemedInputContainerProps, "children"> & {
        step?: number;
        mask?: MasksType;
        minValue?: number;
        maxValue?: number;
        disabled?: boolean;
        placeholder?: string;
        height?: string | number;
        secureTextEntry?: boolean;
        showNumberControls?: boolean;
        onSubmitEditing?: () => void;
        value?: string | number | undefined;
        onChangeText?: (text: string) => void;
        keyboardType?: "default" | "email-address" | "numeric" | "number" | "decimal";
    };



export const ThemedInput = forwardRef<HTMLDivElement, InputProps>(
    (
        {
            icon,
            mask,
            rows,
            label,
            value,
            height,
            inputId,
            maxValue,
            step = 1,
            minValue,
            multiline,
            inputMode,
            helperText,
            placeholder,
            onChangeText,
            heightContent,
            onSubmitEditing,
            disabled = false,
            secureTextEntry = false,
            keyboardType = "default",
            showNumberControls = false,
            ...rest
        },
        ref
    ) => {
        const theme = useTheme();
        const { colors } = useThemeApp();
        const [showPassword, setShowPassword] = useState(false);

        const [inputValue, setInputValue] = useState<string>(() => {
            if (value === undefined || value === null) return "";
            return String(value);
        });

        const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
        const iconSize = isSmallScreen ? 20 : 24;

        useEffect(() => {
            if (value === undefined || value === null) {
                setInputValue("");
            } else {
                setInputValue(String(value));
            }
        }, [value]);

        const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            let rawValue = event.target.value || "";
            let newValue = rawValue;

            const isCurrencyMask = mask === "currency";
            const isNumericKeyboard = keyboardType === "numeric" || keyboardType === "number";

            if (isNumericKeyboard && !isCurrencyMask && !mask) {
                newValue = rawValue.replace(/[^0-9]/g, '');
            }

            if (mask && masks[mask]) {
                try {
                    if (isCurrencyMask && isNumericKeyboard) {
                        const onlyNumbers = rawValue.replace(/\D/g, '');
                        newValue = masks[mask](onlyNumbers);
                    } else {
                        newValue = masks[mask](newValue);
                    }
                } catch (error) {
                    console.error("Error applying mask:", error);
                    newValue = newValue;
                }
            }

            setInputValue(newValue);
            onChangeText && onChangeText(newValue);
        };

        const handleNumberChange = (increment: number) => {
            const currentValueStr = String(inputValue || "");

            if (mask === "currency") {
                let currentNumeric = 0;
                if (currentValueStr) {
                    let cleanValue = currentValueStr
                        .replace(/R\$\s*/g, '')
                        .replace(/\./g, '')
                        .replace(',', '.');

                    currentNumeric = parseFloat(cleanValue) || 0;
                }

                currentNumeric = Math.round(currentNumeric * 100) / 100;

                let newNumeric = currentNumeric + (increment / 100);

                newNumeric = Math.round(newNumeric * 100) / 100;

                if (minValue !== undefined && newNumeric < minValue) newNumeric = minValue;
                if (maxValue !== undefined && newNumeric > maxValue) newNumeric = maxValue;

                const centsValue = Math.round(newNumeric * 100);
                const newValueStr = masks[mask](String(centsValue));

                setInputValue(newValueStr);
                onChangeText && onChangeText(newValueStr);
                return;
            }
            const currentValue = parseInt(currentValueStr) || 0;
            let newValue = currentValue + increment;

            if (minValue !== undefined && newValue < minValue) newValue = minValue;
            if (maxValue !== undefined && newValue > maxValue) newValue = maxValue;

            const newValueStr = String(newValue);
            setInputValue(newValueStr);
            onChangeText && onChangeText(newValueStr);
        };

        const handleKeyDown = (event: React.KeyboardEvent) => {
            if (event.key === "Enter" && onSubmitEditing && !multiline) {
                onSubmitEditing();
                event.preventDefault();
            }

            const supportsNumberControls = showNumberControls && (
                keyboardType === "numeric" ||
                keyboardType === "number" ||
                mask === "currency"
            );

            if (supportsNumberControls) {
                if (event.key === "ArrowUp") {
                    event.preventDefault();
                    handleNumberChange(step);
                } else if (event.key === "ArrowDown") {
                    event.preventDefault();
                    handleNumberChange(-step);
                }
            }
        };

        const getInputType = () => {
            if (secureTextEntry && !showPassword) return "password";
            if (mask === "currency") {
                return "text";
            }
            return "text";
        };

        const getInputProps = () => {
            const props: any = {
                inputMode: inputMode || (keyboardType === "numeric" || keyboardType === "number" ? "numeric" : undefined),
                onKeyDown: handleKeyDown,
            };

            const endAdornments = [];

            const showControls = showNumberControls && (
                keyboardType === "numeric" ||
                keyboardType === "number" ||
                mask === "currency"
            ) && !disabled;

            if (showControls) {
                const currentValueStr = String(inputValue || "");

                let isMaxReached = false;
                let isMinReached = false;

                if (mask === "currency") {
                    const cleanValue = currentValueStr.replace(/R\$\s*/g, '').replace(/\./g, '').replace(',', '.');
                    const currentNumeric = parseFloat(cleanValue) || 0;
                    isMaxReached = maxValue !== undefined && currentNumeric >= maxValue;
                    isMinReached = minValue !== undefined && currentNumeric <= minValue;
                } else {
                    const currentNumber = parseInt(currentValueStr) || 0;
                    isMaxReached = maxValue !== undefined && currentNumber >= maxValue;
                    isMinReached = minValue !== undefined && currentNumber <= minValue;
                }

                endAdornments.push(
                    <Box
                        key="number-controls"
                        sx={{
                            ml: 1,
                            gap: 0.9,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={() => handleNumberChange(step)}
                            disabled={disabled || isMaxReached}
                            sx={{
                                p: 0.9,
                                height: '20px',
                                minWidth: '24px',
                                color: colors.primary,
                                borderRadius: '4px 4px 0 0',
                                bgcolor: colors.primary + '20',
                                '&:hover': {
                                    bgcolor: colors.primary + '40',
                                },
                                '&:disabled': {
                                    opacity: 0.3,
                                }
                            }}
                        >
                            <ThemedIcon
                                size={16}
                                color="primary"
                                type="material"
                                name="ExpandLess"
                            />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => handleNumberChange(-step)}
                            disabled={disabled || isMinReached}
                            sx={{
                                p: 0.9,
                                height: '20px',
                                minWidth: '24px',
                                color: colors.primary,
                                borderRadius: '0 0 4px 4px',
                                bgcolor: colors.primary + '20',
                                '&:hover': {
                                    bgcolor: colors.primary + '40',
                                },
                                '&:disabled': {
                                    opacity: 0.3,
                                }
                            }}
                        >
                            <ThemedIcon
                                size={16}
                                type="material"
                                color="primary"
                                name="ExpandMore"
                            />
                        </IconButton>
                    </Box>
                );
            }

            if (secureTextEntry) {
                endAdornments.push(
                    <IconButton
                        edge="end"
                        size="small"
                        key="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                    >
                        <ThemedIcon
                            size={iconSize}
                            color={"border"}
                            animation="fade"
                            type={"material"}
                            name={showPassword ? "VisibilityOff" : "Visibility"}
                        />
                    </IconButton>
                );
            }

            if (endAdornments.length > 0) {
                props.endAdornment = (
                    <InputAdornment position="end" sx={{ padding: "0px !important" }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {endAdornments}
                        </Box>
                    </InputAdornment>
                );
            }

            return props;
        };

        return (
            <ThemedInputContainer
                icon={icon}
                inputId={inputId}
                disabled={disabled}
                multiline={multiline}
                helperText={helperText}
                heightContent={heightContent}
                label={label ? label : undefined}
                justifyContentCenter={!multiline}
            >
                <TextField
                    fullWidth
                    inputRef={ref}
                    value={inputValue}
                    variant="outlined"
                    disabled={disabled}
                    multiline={multiline}
                    type={getInputType()}
                    onChange={handleOnChange}
                    InputProps={getInputProps()}
                    placeholder={placeholder || undefined}
                    rows={multiline ? (rows || 4) : undefined}
                    inputProps={{
                        inputMode: inputMode || (keyboardType === "numeric" || keyboardType === "number" ? "numeric" : undefined),
                        style: {
                            paddingTop: multiline ? 12 : undefined,
                            paddingBottom: multiline ? 12 : undefined,
                            textAlign: showNumberControls ? 'center' : 'left',
                        },
                    }}
                    sx={{
                        height: "100%",
                        padding: "0px !important",
                        backgroundColor: "transparent !important",
                        "& .MuiInputBase-input": {
                            padding: "0px !important",
                        },
                        "& input[type=number]": {
                            MozAppearance: 'textfield',
                        },
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                            display: 'none',
                            WebkitAppearance: 'none',
                        },
                        "& .MuiOutlinedInput-root": {
                            padding: "0px !important",
                            backgroundColor: "transparent !important",
                        }
                    }}
                    {...rest}
                />
            </ThemedInputContainer>
        );
    }
);

ThemedInput.displayName = "ThemedInput";